import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { MemberDashboard } from './MemberDashboard';
import { toast } from 'react-hot-toast';
import { Users, Plus, FileEdit as Edit, Trash2, ArrowLeft, LogOut, RefreshCw, Calendar, Mail, Hash, Euro, User, Upload, Database, FileSpreadsheet, Eye, FileText, X, Download, BarChart3, Share2, Bug, CheckCircle, XCircle, Clock, UserCheck, RotateCcw, Calculator, Pencil, Link, Save, Building2, Ban, Search } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { Database as DB } from '../types/supabase';
import { fetchTariffOverridesBatch, resolvePriceForMonth, type TariffOverride } from '../utils/tariffOverrides';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import { ParticipantForm } from './ParticipantForm';
import { MonthlyFileManager } from './MonthlyFileManager';
import { useAutoLogout } from '../hooks/useAutoLogout';
import { InvoiceTemplate, removeGroupePrefix, normalizeFileName, getGroupCompanies } from './InvoiceTemplate';
import { isSupabaseConfigured } from '../lib/supabase';
import AdminEanChart from './AdminEanChart';
import { ParticipantReportModal } from './ParticipantReportModal';
import { GroupReportModal } from './GroupReportModal';
import { ExportDataModal } from './ExportDataModal';
import { AccessInvitationModal } from './AccessInvitationModal';

type Participant = DB['public']['Tables']['participants']['Row'];
type AdminTab = 'sharing-data' | 'participants' | 'debug';

export function AdminDashboard() {
  const [activeTab, setActiveTab] = useState<AdminTab>('sharing-data');
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [tariffOverridesMap, setTariffOverridesMap] = useState<Record<string, TariffOverride[]>>({});
  const [loading, setLoading] = useState(true);
  const [sharingOperationId, setSharingOperationId] = useState<string | null>(null);
  const [sharingOperationName, setSharingOperationName] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [editingParticipant, setEditingParticipant] = useState<Participant | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [viewingMemberDashboard, setViewingMemberDashboard] = useState<any>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [selectedParticipantForInvoice, setSelectedParticipantForInvoice] = useState<Participant | null>(null);
  const [showPeriodSelection, setShowPeriodSelection] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState({
    startMonth: '',
    endMonth: ''
  });
  const [isAnnualRegularization, setIsAnnualRegularization] = useState(false);
  const [showBulkPeriodSelection, setShowBulkPeriodSelection] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [showBulkDownloadModal, setShowBulkDownloadModal] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState<Set<string>>(new Set());
  const [bulkPeriod, setBulkPeriod] = useState({ startMonth: '', endMonth: '' });
  const [bulkIncludeMembershipFee, setBulkIncludeMembershipFee] = useState(false);
  const [bulkExcludeMembershipFee, setBulkExcludeMembershipFee] = useState(false);
  const [bulkIsAnnualRegularization, setBulkIsAnnualRegularization] = useState(false);
  const [bulkFixIssueDate, setBulkFixIssueDate] = useState(false);
  const [bulkIssueDate, setBulkIssueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [showCompanySelector, setShowCompanySelector] = useState(false);
  const [companySelectorItems, setCompanySelectorItems] = useState<{ company_number: string | null; company_name: string | null; representative: any }[]>([]);
  const [loadingCompanySelector, setLoadingCompanySelector] = useState(false);
  const [invoiceCompanyFilter, setInvoiceCompanyFilter] = useState<string | null | undefined>(undefined);
  const [selectedCompanyIndices, setSelectedCompanyIndices] = useState<Set<number>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [csvUrl, setCsvUrl] = useState("");
  const [savedLinks, setSavedLinks] = useState<any[]>([]);
  const [showSavedLinks, setShowSavedLinks] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState<Set<string>>(new Set());
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [newLinkLabel, setNewLinkLabel] = useState("");
  const [newLinkMonth, setNewLinkMonth] = useState("");
  const [savingLink, setSavingLink] = useState(false);
  const [renamingLinkId, setRenamingLinkId] = useState<string | null>(null);
  const [renamingLinkValue, setRenamingLinkValue] = useState("");
  const [sortBy, setSortBy] = useState<'name' | 'type' | 'entry_date' | 'company_name'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filterSousPartage, setFilterSousPartage] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedParticipantForReport, setSelectedParticipantForReport] = useState<Participant | null>(null);
  const [showGroupReportModal, setShowGroupReportModal] = useState(false);
  const [selectedGroupForReport, setSelectedGroupForReport] = useState<{ name: string; participants: Participant[] } | null>(null);
  const [showBulkReportModal, setShowBulkReportModal] = useState(false);
  const [selectedReportParticipants, setSelectedReportParticipants] = useState<Set<string>>(new Set());
  const [bulkReportYear, setBulkReportYear] = useState<string>(new Date().getFullYear().toString());
  const [bulkReportDownloading, setBulkReportDownloading] = useState(false);
  const [showExportData, setShowExportData] = useState(false);
  const [selectedParticipantForExport, setSelectedParticipantForExport] = useState<Participant | null>(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [selectedParticipantForValidation, setSelectedParticipantForValidation] = useState<Participant | null>(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [pendingAccounts, setPendingAccounts] = useState<any[]>([]);
  const [selectedAccountForValidation, setSelectedAccountForValidation] = useState<any | null>(null);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [volumeFilterStart, setVolumeFilterStart] = useState('');
  const [volumeFilterEnd, setVolumeFilterEnd] = useState(() => new Date().toISOString().slice(0, 7));
  const [volumeFilterParticipant, setVolumeFilterParticipant] = useState('all');
  const [billingConfig, setBillingConfig] = useState<any>(null);
  const [allIdxValues, setAllIdxValues] = useState<Record<string, Record<string, number>>>({});
  const [dualRoleServices, setDualRoleServices] = useState<string[]>([]);
  const [adminPermissions, setAdminPermissions] = useState<{
    generate_invoice: boolean;
    generate_report: boolean;
    add_member: boolean;
    edit_member: boolean;
    delete_member: boolean;
    import_data: boolean;
    donnees_qh: boolean;
  } | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<{
    isConnected: boolean;
    userEmail: string | null;
    hasValidSession: boolean;
    canAccessParticipants: boolean;
    errorDetails: string | null;
  }>({
    isConnected: false,
    userEmail: null,
    hasValidSession: false,
    canAccessParticipants: false,
    errorDetails: null
  });
// Fonction pour convertir automatiquement un lien Google Sheet en lien CSV
function convertGoogleSheetUrl(url: string) {
  if (!url) return "";
  try {
    const gidMatch = url.match(/\/d\/([a-zA-Z0-9-_]+)\/.*[?&]gid=([0-9]+)/);
    if (gidMatch) {
      const fileId = gidMatch[1];
      const gid = gidMatch[2];
      return `https://docs.google.com/spreadsheets/d/${fileId}/export?format=csv&gid=${gid}`;
    }

    const simpleMatch = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
    if (simpleMatch) {
      const fileId = simpleMatch[1];
      return `https://docs.google.com/spreadsheets/d/${fileId}/export?format=csv&gid=0`;
    }

    return url;
  } catch {
    return url;
  }
}

  useEffect(() => {
    const initializeDashboard = async () => {
      // Charger l'opération de partage sélectionnée
      const selectedOpId = localStorage.getItem('selected_sharing_operation_id');
      if (!selectedOpId) {
        toast.error('Aucune opération sélectionnée');
        window.location.href = '/admin/operations';
        return;
      }

      // Vérifier l'accès à cette opération
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Non authentifié');
        window.location.href = '/';
        return;
      }

      const superAdmin = user.email === 'info@sunisup.be';
      setIsSuperAdmin(superAdmin);

      const storedDualRole = localStorage.getItem('dual_role_services');
      if (storedDualRole) {
        try { setDualRoleServices(JSON.parse(storedDualRole)); } catch { /* ignore */ }
      }

      // Charger les détails de l'opération
      const { data: operation, error: opError } = await supabase
        .from('sharing_operations')
        .select('*')
        .eq('id', selectedOpId)
        .maybeSingle();

      if (opError || !operation) {
        toast.error('Opération introuvable');
        window.location.href = '/admin/operations';
        return;
      }

      // Si pas superadmin, vérifier qu'il est admin de cette opération
      if (!superAdmin) {
        const { data: adminOp } = await supabase
          .from('sharing_operation_admins')
          .select('*')
          .eq('sharing_operation_id', selectedOpId)
          .eq('user_id', user.id)
          .not('invitation_accepted_at', 'is', null)
          .maybeSingle();

        if (!adminOp) {
          toast.error('Accès refusé à cette opération');
          window.location.href = '/admin/operations';
          return;
        }

        const defaultPerms = {
          generate_invoice: true,
          generate_report: true,
          add_member: true,
          edit_member: true,
          delete_member: true,
          import_data: true,
          donnees_qh: true,
        };
        setAdminPermissions((adminOp as any).permissions ?? defaultPerms);
      }

      setSharingOperationId(selectedOpId);
      setSharingOperationName(operation.name);
      verifyAdminConnection();
      loadParticipants(selectedOpId);
    };

    initializeDashboard();
  }, []);

  useEffect(() => {
    const loadCostConfig = async () => {
      try {
        const { data: bc } = await supabase.from('billing_config').select('*').maybeSingle();
        if (bc) setBillingConfig(bc);

        const { data: idxRows } = await supabase.from('indexation_parameters').select('param_name, month_key, value');
        if (idxRows) {
          const map: Record<string, Record<string, number>> = {};
          for (const row of idxRows) {
            if (!map[row.param_name]) map[row.param_name] = {};
            map[row.param_name][row.month_key] = Number(row.value);
          }
          setAllIdxValues(map);
        }
      } catch {
        // non-blocking
      }
    };
    loadCostConfig();
  }, []);

  // Ajouter un état pour forcer l'affichage du debug
  const [showDebugInfo, setShowDebugInfo] = useState(true);
  const [debugMessages, setDebugMessages] = useState<string[]>([]);

  const can = (permission: 'generate_invoice' | 'generate_report' | 'add_member' | 'edit_member' | 'delete_member' | 'import_data' | 'donnees_qh'): boolean => {
    if (isSuperAdmin) return true;
    if (!adminPermissions) return true;
    return adminPermissions[permission] === true;
  };

  const addDebugMessage = (message: string) => {
    console.log(message);
    setDebugMessages(prev => [...prev.slice(-10), `${new Date().toLocaleTimeString()}: ${message}`]);
  };
  const verifyAdminConnection = async () => {
    addDebugMessage('🔍 DÉBUT VÉRIFICATION CONNEXION ADMIN SUPABASE');
    
    try {
      // 1. Vérifier la session actuelle
      addDebugMessage('📋 Étape 1: Vérification de la session...');
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        addDebugMessage(`❌ Erreur session: ${sessionError.message}`);
        setConnectionStatus({
          isConnected: false,
          userEmail: null,
          hasValidSession: false,
          canAccessParticipants: false,
          errorDetails: `Erreur session: ${sessionError.message}`
        });
        return;
      }

      if (!session) {
        addDebugMessage('⚠️ Aucune session active');
        setConnectionStatus({
          isConnected: false,
          userEmail: null,
          hasValidSession: false,
          canAccessParticipants: false,
          errorDetails: 'Aucune session active'
        });
        return;
      }

      addDebugMessage(`✅ Session trouvée: ${session.user.email}, expires: ${session.expires_at}`);

      // 2. Vérifier l'utilisateur actuel
      addDebugMessage('📋 Étape 2: Vérification de l\'utilisateur...');
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        addDebugMessage(`❌ Erreur utilisateur: ${userError.message}`);
        setConnectionStatus({
          isConnected: false,
          userEmail: session.user.email,
          hasValidSession: true,
          canAccessParticipants: false,
          errorDetails: `Erreur utilisateur: ${userError.message}`
        });
        return;
      }

      addDebugMessage(`✅ Utilisateur vérifié: ${user?.email}, isAdmin: ${user?.email === 'info@sunisup.be'}`);

      // 3. Tester l'accès à la table participants
      addDebugMessage('📋 Étape 3: Test d\'accès à la table participants...');
      const { data: testData, error: accessError } = await supabase
        .from('participants')
        .select('id, name, email')
        .limit(1);

      if (accessError) {
        addDebugMessage(`❌ Erreur accès participants: Code ${accessError.code}, Message: ${accessError.message}`);
        
        setConnectionStatus({
          isConnected: true,
          userEmail: user?.email || null,
          hasValidSession: true,
          canAccessParticipants: false,
          errorDetails: `Accès refusé: ${accessError.message} (Code: ${accessError.code})`
        });
        return;
      }

      addDebugMessage(`✅ Accès participants réussi: ${testData?.length || 0} participants trouvés`);

      // 4. Tester une opération d'écriture
      addDebugMessage('📋 Étape 4: Test d\'écriture (simulation)...');
      const { error: writeTestError } = await supabase
        .from('participants')
        .select('id')
        .limit(0); // Ne récupère rien mais teste les permissions

      if (writeTestError) {
        addDebugMessage(`⚠️ Erreur test écriture: ${writeTestError.message}`);
      }

      setConnectionStatus({
        isConnected: true,
        userEmail: user?.email || null,
        hasValidSession: true,
        canAccessParticipants: true,
        errorDetails: null
      });

      addDebugMessage('🎉 CONNEXION ADMIN VÉRIFIÉE AVEC SUCCÈS');
      toast.success(`✅ Connexion admin vérifiée pour ${user?.email}`);

    } catch (error: any) {
      addDebugMessage(`❌ ERREUR GÉNÉRALE: ${error.message}`);
      setConnectionStatus({
        isConnected: false,
        userEmail: null,
        hasValidSession: false,
        canAccessParticipants: false,
        errorDetails: `Erreur générale: ${error.message}`
      });
    }
  };
  const loadParticipants = async (operationId?: string) => {
    setLoading(true);
    const opId = operationId || sharingOperationId;
    try {
      if (!isSupabaseConfigured) {
        console.warn('Supabase not configured');
        setParticipants([]);
        return;
      }

      if (!opId) {
        console.warn('No sharing operation selected');
        setParticipants([]);
        return;
      }

      console.log('📊 CHARGEMENT PARTICIPANTS ADMIN pour', opId);

      const { data, error } = await supabase
        .from('participants')
        .select('*')
        .eq('sharing_operation_id', opId)
        .order(sortBy, { ascending: sortOrder === 'asc' });

      if (error) {
        console.error('❌ Error loading participants:', error);
        toast.error(`Erreur de chargement: ${error.message}`);
        setParticipants([]);
        return;
      }

      console.log('✅ Loaded', data?.length || 0, 'participants');
      setParticipants(data || []);

      // Load pending multi-account requests for these participants
      if (data && data.length > 0) {
        const ids = data.map(p => p.id).filter(Boolean);
        fetchTariffOverridesBatch(ids).then(setTariffOverridesMap).catch(() => {});

        const { data: accounts, error: accountsError } = await supabase
          .from('participant_accounts')
          .select('id, participant_id, email, account_status, account_request_date')
          .in('participant_id', ids)
          .order('account_request_date', { ascending: false });

        if (!accountsError && accounts) {
          setPendingAccounts(accounts);
        } else {
          setPendingAccounts([]);
        }
      } else {
        setPendingAccounts([]);
      }

      if (data && data.length > 0) {
        toast.success(`${data.length} participants chargés`);
      }

    } catch (error) {
      console.error('❌ Error loading participants:', error);
      toast.error(`Erreur: ${error.message}`);
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour changer le tri
  const handleSort = (column: 'name' | 'type' | 'entry_date' | 'company_name') => {
    if (sortBy === column) {
      // Si on clique sur la même colonne, inverser l'ordre
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Si on clique sur une nouvelle colonne, commencer par ordre croissant
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Recharger les données quand le tri change
  useEffect(() => {
    if (!loading) {
      loadParticipants();
    }
  }, [sortBy, sortOrder]);

  // CRM → Communications → À faire → « Générer les factures » :
  // ouvre l'outil de factures groupées avec les participants pré-cochés et la période remplie.
  useEffect(() => {
    if (!sharingOperationId || participants.length === 0) return;
    let raw: string | null = null;
    try { raw = localStorage.getItem('crm_invoice_prefill'); } catch { return; }
    if (!raw) return;
    try {
      const prefill = JSON.parse(raw) as { operationId: string; startMonth: string; endMonth: string; participantIds: string[]; createdAt: number };
      if (prefill.operationId !== sharingOperationId) return;
      localStorage.removeItem('crm_invoice_prefill');
      if (Date.now() - prefill.createdAt > 15 * 60 * 1000) return;
      const known = new Set(participants.map(p => p.id));
      const ids = prefill.participantIds.filter(id => known.has(id));
      setSelectedParticipants(new Set(ids));
      setBulkPeriod({ startMonth: prefill.startMonth, endMonth: prefill.endMonth });
      setShowBulkDownloadModal(true);
      toast.success(`${ids.length} participant${ids.length > 1 ? 's' : ''} pré-coché${ids.length > 1 ? 's' : ''} depuis la todolist du CRM`);
    } catch {
      try { localStorage.removeItem('crm_invoice_prefill'); } catch { /* rien */ }
    }
  }, [sharingOperationId, participants]);

  // Actualiser les données
  const handleRefreshData = async () => {
    setRefreshing(true);
    try {
      await loadParticipants();
      console.log('🔄 Données actualisées');
    } catch (error) {
      console.error('❌ Erreur actualisation:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Fonction pour grouper les participants
  const groupParticipants = () => {
    const grouped: { [key: string]: Participant[] } = {};
    const ungrouped: Participant[] = [];

    const filtered = participants.filter(p => {
      if (filterSousPartage && (p as any).sous_partage !== filterSousPartage) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const name = (p.name || '').toLowerCase();
        const ean = (p.ean_code || '').toLowerCase();
        const company = ((p as any).company_name || '').toLowerCase();
        const group = (p.groupe || '').toLowerCase();
        if (!name.includes(q) && !ean.includes(q) && !company.includes(q) && !group.includes(q)) return false;
      }
      return true;
    });

    filtered.forEach(participant => {
      if (participant.groupe) {
        if (!grouped[participant.groupe]) {
          grouped[participant.groupe] = [];
        }
        grouped[participant.groupe].push(participant);
      } else {
        ungrouped.push(participant);
      }
    });

    return { grouped, ungrouped };
  };

  const isAccountActivated = (participantOrGroup: Participant | Participant[]): boolean => {
    if (Array.isArray(participantOrGroup)) {
      return participantOrGroup.some(p => p.email && p.email.trim() !== '');
    }
    return participantOrGroup.email && participantOrGroup.email.trim() !== '';
  };

  const getAccountStatus = (participant: Participant): 'pending' | 'approved' | 'rejected' | 'none' => {
    if (!participant.email) return 'none';
    const hasPendingAccount = pendingAccounts.some(
      a => a.participant_id === participant.id && a.account_status === 'pending'
    );
    if (hasPendingAccount) return 'pending';
    return (participant as any).account_status || 'pending';
  };

  const handleValidationAction = async (action: 'approve' | 'reject') => {
    if (!selectedParticipantForValidation) return;

    setValidationLoading(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(`${supabaseUrl}/functions/v1/account-validation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          action,
          participantId: selectedParticipantForValidation.id
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors de la validation');
      }

      toast.success(action === 'approve' ? 'Compte approuve avec succes' : 'Compte refuse');
      setShowValidationModal(false);
      setSelectedParticipantForValidation(null);
      loadParticipants();
    } catch (error: any) {
      console.error('Validation error:', error);
      toast.error(error.message || 'Erreur lors de la validation');
    } finally {
      setValidationLoading(false);
    }
  };

  const handleAccountValidationAction = async (action: 'approve_account' | 'reject_account') => {
    if (!selectedAccountForValidation) return;

    setValidationLoading(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(`${supabaseUrl}/functions/v1/account-validation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          action,
          account_id: selectedAccountForValidation.id
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors de la validation');
      }

      toast.success(action === 'approve_account' ? 'Compte approuve avec succes' : 'Compte refuse');
      setShowValidationModal(false);
      setSelectedAccountForValidation(null);
      loadParticipants();
    } catch (error: any) {
      console.error('Validation error:', error);
      toast.error(error.message || 'Erreur lors de la validation');
    } finally {
      setValidationLoading(false);
    }
  };

  const toggleGroup = (groupName: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName);
    } else {
      newExpanded.add(groupName);
    }
    setExpandedGroups(newExpanded);
  };

  const handleGroupAction = (action: 'invoice' | 'dashboard' | 'edit', group: Participant[]) => {
    // Pour les actions de groupe, utiliser le premier participant du groupe
    const representative = group[0];
    
    switch (action) {
      case 'invoice':
        handleShowInvoice(representative);
        break;
      case 'dashboard':
        handleViewParticipantDashboard(representative);
        break;
      case 'edit':
        handleEdit(representative);
        break;
    }
  };

  const handleEdit = (participant: Participant) => {
    setEditingParticipant(participant);
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditingParticipant(null);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingParticipant(null);
    loadParticipants();
  };


  const handleViewParticipantDashboard = async (participant: Participant) => {
    const userObj = {
      id: participant.id,
      participant_id: participant.id,
      email: participant.email || null,
      name: participant.name,
      member_type: participant.type,
      user_metadata: {
        participant_id: participant.id,
        ean_code: participant.ean_code,
        name: participant.name
      }
    };

    setViewingMemberDashboard(userObj);
  };

  const handleCloseMemberDashboard = () => {
    setViewingMemberDashboard(null);
  };

  const handleShowInvoice = (participant: Participant) => {
    console.log('🧾 CLIC GÉNÉRATION FACTURE');
    console.log('👤 Participant:', participant);
    console.log('📧 Email participant:', participant.email);
    console.log('🆔 ID participant:', participant.id);

    setSelectedParticipantForInvoice(participant);
    console.log('✅ Participant sélectionné pour facture');

    setShowPeriodSelection(true);
    console.log('✅ Modal période activée');

    // Initialiser avec le mois actuel
    const currentMonth = new Date().toISOString().slice(0, 7);
    setSelectedPeriod({
      startMonth: currentMonth,
      endMonth: currentMonth
    });

    setBulkIncludeMembershipFee(false);
    setBulkExcludeMembershipFee(false);
    console.log('✅ Période initialisée:', currentMonth);
  };

  const handleCloseInvoice = () => {
    setShowInvoice(false);
    setSelectedParticipantForInvoice(null);
    setShowPeriodSelection(false);
    setSelectedPeriod({ startMonth: '', endMonth: '' });
    setBulkIncludeMembershipFee(false);
    setBulkExcludeMembershipFee(false);
    setInvoiceCompanyFilter(undefined);
  };

  const handlePeriodConfirmByCompany = async () => {
    if (!selectedPeriod.startMonth || !selectedPeriod.endMonth) {
      toast.error('Veuillez sélectionner une période valide');
      return;
    }
    if (selectedPeriod.startMonth > selectedPeriod.endMonth) {
      toast.error('Le mois de début doit être antérieur ou égal au mois de fin');
      return;
    }
    if (!selectedParticipantForInvoice?.groupe) return;

    setLoadingCompanySelector(true);
    try {
      const companies = await getGroupCompanies(selectedParticipantForInvoice.groupe);
      const items = companies.map(c => ({
        company_number: c.company_number,
        company_name: c.company_name,
        representative: c.participants[0]
      }));
      setCompanySelectorItems(items);
      setSelectedCompanyIndices(new Set(items.map((_, i) => i)));
      setBulkIncludeMembershipFee(false);
      setBulkExcludeMembershipFee(false);
      setShowPeriodSelection(false);
      setShowCompanySelector(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des entreprises');
    } finally {
      setLoadingCompanySelector(false);
    }
  };

  const handleGenerateSelectedCompanies = async () => {
    const selected = Array.from(selectedCompanyIndices);
    if (selected.length === 0) {
      toast.error('Veuillez sélectionner au moins une entreprise');
      return;
    }

    if (selected.length === 1) {
      const company = companySelectorItems[selected[0]];
      setInvoiceCompanyFilter(company.company_number);
      setSelectedParticipantForInvoice(company.representative);
      setShowCompanySelector(false);
      setShowInvoice(true);
      await saveIssueDatesToDb(
        [{ participant: company.representative, companyNumberFilter: company.company_number }],
        selectedPeriod
      );
      return;
    }

    setShowCompanySelector(false);
    setBulkDownloading(true);

    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const { generateInvoicePDF } = await import('./InvoiceTemplate');

      const toastId = toast.loading(`Génération de ${selected.length} factures...`);
      let successCount = 0;
      let errorCount = 0;
      const failedItems: string[] = [];

      for (let i = 0; i < selected.length; i++) {
        const company = companySelectorItems[selected[i]];
        const label = company.company_name || (company.company_number ? `N°${company.company_number}` : 'SansEntreprise');
        toast.loading(`Génération ${i + 1}/${selected.length}: ${label}...`, { id: toastId });

        try {
          const pdfBlob = await generateInvoicePDF(
            company.representative,
            selectedPeriod,
            bulkExcludeMembershipFee ? false : (bulkIncludeMembershipFee ? true : undefined),
            company.representative.type !== 'producer' && company.representative.type !== 'prosumer' ? isAnnualRegularization : undefined,
            company.company_number,
            bulkFixIssueDate ? bulkIssueDate : null
          );
          if (pdfBlob) {
            const cleanName = normalizeFileName(label);
            zip.file(`Facture_${cleanName}_${selectedPeriod.startMonth}-${selectedPeriod.endMonth}.pdf`, pdfBlob);
            successCount++;
          } else {
            errorCount++;
            failedItems.push(label);
          }
        } catch (err) {
          console.error(`Erreur génération facture pour ${label}:`, err);
          errorCount++;
          failedItems.push(label);
        }

        await new Promise(r => setTimeout(r, 200));
      }

      toast.dismiss(toastId);

      if (successCount === 0) {
        toast.error("Aucune facture n'a pu être générée");
        return;
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Factures_${selectedPeriod.startMonth}-${selectedPeriod.endMonth}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (errorCount > 0) {
        toast.success(`${successCount} factures téléchargées`);
        toast.error(`${errorCount} erreur(s) : ${failedItems.slice(0, 3).join(', ')}${failedItems.length > 3 ? '...' : ''}`, { duration: 6000 });
      } else {
        toast.success(`${successCount} factures téléchargées avec succès`);
      }

      await saveIssueDatesToDb(
        selected.map(idx => companySelectorItems[idx]).map(c => ({
          participant: c.representative,
          companyNumberFilter: c.company_number,
        })),
        selectedPeriod
      );
    } catch (error) {
      toast.dismiss();
      toast.error('Erreur lors de la génération des factures');
    } finally {
      setBulkDownloading(false);
    }
  };

  const saveIssueDatesToDb = async (
    jobs: { participant: Participant; companyNumberFilter?: string | null }[],
    period: { startMonth: string; endMonth: string }
  ) => {
    if (!bulkFixIssueDate || !sharingOperationId) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const adminEmail = user?.email || null;
      for (const job of jobs) {
        const groupKey = job.participant.groupe || null;
        const companyNumber = job.companyNumberFilter ?? null;

        let existingQuery = supabase
          .from('invoice_issue_dates')
          .select('id')
          .eq('sharing_operation_id', sharingOperationId)
          .eq('period_start', period.startMonth)
          .eq('period_end', period.endMonth);
        if (groupKey) {
          existingQuery = existingQuery.eq('group_key', groupKey);
        } else {
          existingQuery = existingQuery.is('group_key', null);
        }
        if (companyNumber) {
          existingQuery = existingQuery.eq('company_number', companyNumber);
        } else {
          existingQuery = existingQuery.is('company_number', null);
        }
        const { data: existing } = await existingQuery.maybeSingle();

        if (existing) {
          await supabase
            .from('invoice_issue_dates')
            .update({ issue_date: bulkIssueDate, set_by_email: adminEmail, updated_at: new Date().toISOString() })
            .eq('id', existing.id);
        } else {
          await supabase
            .from('invoice_issue_dates')
            .insert({
              sharing_operation_id: sharingOperationId,
              group_key: groupKey,
              company_number: companyNumber,
              period_start: period.startMonth,
              period_end: period.endMonth,
              issue_date: bulkIssueDate,
              set_by_email: adminEmail,
            });
        }
      }
    } catch (err) {
      console.error('Error saving issue dates:', err);
    }
  };

  const handlePeriodConfirm = () => {
    if (!selectedPeriod.startMonth || !selectedPeriod.endMonth) {
      toast.error('Veuillez sélectionner une période valide');
      return;
    }
    if (selectedPeriod.startMonth > selectedPeriod.endMonth) {
      toast.error('Le mois de début doit être antérieur ou égal au mois de fin');
      return;
    }

    setShowPeriodSelection(false);
    setShowInvoice(true);

    if (bulkFixIssueDate && selectedParticipantForInvoice) {
      saveIssueDatesToDb(
        [{ participant: selectedParticipantForInvoice, companyNumberFilter: invoiceCompanyFilter ?? null }],
        selectedPeriod
      );
    }
  };

  const handleBulkDownload = async () => {
    if (!bulkPeriod.startMonth || !bulkPeriod.endMonth) {
      toast.error('Veuillez sélectionner une période valide');
      return;
    }
    if (bulkPeriod.startMonth > bulkPeriod.endMonth) {
      toast.error('Le mois de début doit être antérieur ou égal au mois de fin');
      return;
    }
    if (selectedParticipants.size === 0) {
      toast.error('Veuillez sélectionner au moins un participant');
      return;
    }

    setBulkDownloading(true);
    setShowBulkDownloadModal(false);

    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      // Filter selected participants
      const participantsToProcess = participants.filter(p => selectedParticipants.has(p.id));

      // Group participants by groupe
      const groupedParticipants = new Map<string, Participant[]>();
      const individualParticipants: Participant[] = [];

      participantsToProcess.forEach(p => {
        if (p.groupe) {
          if (!groupedParticipants.has(p.groupe)) {
            groupedParticipants.set(p.groupe, []);
          }
          groupedParticipants.get(p.groupe)!.push(p);
        } else {
          individualParticipants.push(p);
        }
      });

      // Build list of jobs: each job is either a single participant or a (group, company) pair
      interface InvoiceJob {
        participant: Participant;
        label: string;
        companyNumberFilter?: string | null;
        fileName: string;
      }

      const invoiceJobs: InvoiceJob[] = [];

      // For each group, check if it has multiple companies
      for (const [groupName, group] of groupedParticipants.entries()) {
        const companies = await getGroupCompanies(groupName);
        if (companies.length > 1) {
          for (const company of companies) {
            const companyLabel = company.company_name || (company.company_number ? `N°${company.company_number}` : 'SansEntreprise');
            const groupLabel = removeGroupePrefix(groupName);
            const cleanName = normalizeFileName(`${groupLabel}_${companyLabel}`);
            invoiceJobs.push({
              participant: company.participants[0] as Participant,
              label: `${groupLabel} - ${companyLabel}`,
              companyNumberFilter: company.company_number,
              fileName: `Facture_${cleanName}_${bulkPeriod.startMonth}-${bulkPeriod.endMonth}.pdf`
            });
          }
        } else {
          const rep = group[0];
          const participantName = removeGroupePrefix(groupName);
          const cleanName = normalizeFileName(participantName);
          invoiceJobs.push({
            participant: rep,
            label: participantName,
            companyNumberFilter: undefined,
            fileName: `Facture_${cleanName}_${bulkPeriod.startMonth}-${bulkPeriod.endMonth}.pdf`
          });
        }
      }

      // Add individual participants
      for (const p of individualParticipants) {
        const cleanName = normalizeFileName(p.name);
        invoiceJobs.push({
          participant: p,
          label: p.name,
          companyNumberFilter: undefined,
          fileName: `Facture_${cleanName}_${bulkPeriod.startMonth}-${bulkPeriod.endMonth}.pdf`
        });
      }

      const toastId = toast.loading(`Génération de ${invoiceJobs.length} factures...`);

      const { generateInvoicePDF } = await import('./InvoiceTemplate');

      let successCount = 0;
      let errorCount = 0;
      const failedParticipants: string[] = [];

      for (let i = 0; i < invoiceJobs.length; i++) {
        const job = invoiceJobs[i];
        toast.loading(`Génération ${i + 1}/${invoiceJobs.length}: ${job.label}...`, { id: toastId });

        try {
          const pdfBlob = await generateInvoicePDF(
            job.participant,
            bulkPeriod,
            bulkExcludeMembershipFee ? false : (bulkIncludeMembershipFee ? true : undefined),
            job.participant.type !== 'producer' ? bulkIsAnnualRegularization : undefined,
            job.companyNumberFilter,
            bulkFixIssueDate ? bulkIssueDate : null
          );

          if (pdfBlob) {
            zip.file(job.fileName, pdfBlob);
            successCount++;
          } else {
            errorCount++;
            failedParticipants.push(job.label);
          }
        } catch (error) {
          console.error(`Erreur lors de la génération de la facture pour ${job.label}:`, error);
          errorCount++;
          failedParticipants.push(job.label);
        }

        await new Promise(r => setTimeout(r, 100));
      }

      toast.dismiss(toastId);

      if (successCount === 0) {
        toast.error('Aucune facture n\'a pu être générée');
        setSelectedParticipants(new Set());
        return;
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Factures_${bulkPeriod.startMonth}-${bulkPeriod.endMonth}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (errorCount > 0) {
        toast.success(`${successCount} factures téléchargées`);
        toast.error(`${errorCount} factures en erreur: ${failedParticipants.slice(0, 3).join(', ')}${failedParticipants.length > 3 ? '...' : ''}`, { duration: 6000 });
      } else {
        toast.success(`${successCount} factures téléchargées avec succès`);
      }

      setSelectedParticipants(new Set());

      await saveIssueDatesToDb(
        invoiceJobs.map(j => ({ participant: j.participant, companyNumberFilter: j.companyNumberFilter })),
        bulkPeriod
      );
    } catch (error) {
      console.error('Erreur lors du téléchargement groupé:', error);
      toast.dismiss();
      toast.error('Erreur lors de la génération des factures');
    } finally {
      setBulkDownloading(false);
    }
  };

  const toggleParticipantSelection = (participantId: string) => {
    const newSelection = new Set(selectedParticipants);
    if (newSelection.has(participantId)) {
      newSelection.delete(participantId);
    } else {
      newSelection.add(participantId);
    }
    setSelectedParticipants(newSelection);
  };

  const selectAllParticipants = () => {
    if (selectedParticipants.size === participants.length) {
      setSelectedParticipants(new Set());
    } else {
      setSelectedParticipants(new Set(participants.map(p => p.id)));
    }
  };

  const toggleReportParticipantSelection = (participantId: string) => {
    const newSelection = new Set(selectedReportParticipants);
    if (newSelection.has(participantId)) {
      newSelection.delete(participantId);
    } else {
      newSelection.add(participantId);
    }
    setSelectedReportParticipants(newSelection);
  };

  const selectAllReportParticipants = () => {
    if (selectedReportParticipants.size === participants.length) {
      setSelectedReportParticipants(new Set());
    } else {
      setSelectedReportParticipants(new Set(participants.map(p => p.id)));
    }
  };

  const handleBulkReportDownload = async () => {
    if (selectedReportParticipants.size === 0) {
      toast.error('Veuillez sélectionner au moins un participant');
      return;
    }

    setBulkReportDownloading(true);
    setShowBulkReportModal(false);

    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const { generateReportPDF } = await import('./ParticipantReportModal');

      const participantsToProcess = participants.filter(p => selectedReportParticipants.has(p.id));
      const toastId = toast.loading(`Génération de ${participantsToProcess.length} rapports...`);

      let successCount = 0;
      let errorCount = 0;
      const failedParticipants: string[] = [];

      for (let i = 0; i < participantsToProcess.length; i++) {
        const participant = participantsToProcess[i];
        toast.loading(`Rapport ${i + 1}/${participantsToProcess.length}: ${participant.name}...`, { id: toastId });

        try {
          const pdfBlob = await generateReportPDF(participant, bulkReportYear);
          if (pdfBlob) {
            const cleanName = normalizeFileName(participant.name);
            const fileName = `Rapport_${cleanName}_${bulkReportYear}.pdf`;
            zip.file(fileName, pdfBlob);
            successCount++;
          } else {
            errorCount++;
            failedParticipants.push(participant.name);
          }
        } catch (error) {
          console.error(`Erreur rapport pour ${participant.name}:`, error);
          errorCount++;
          failedParticipants.push(participant.name);
        }

        await new Promise(r => setTimeout(r, 500));
      }

      toast.dismiss(toastId);

      if (successCount === 0) {
        toast.error('Aucun rapport n\'a pu être généré');
        setSelectedReportParticipants(new Set());
        return;
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Rapports_${bulkReportYear}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (errorCount > 0) {
        toast.success(`${successCount} rapports téléchargés`);
        toast.error(`${errorCount} rapports en erreur: ${failedParticipants.slice(0, 3).join(', ')}${failedParticipants.length > 3 ? '...' : ''}`, { duration: 6000 });
      } else {
        toast.success(`${successCount} rapports téléchargés avec succès`);
      }

      setSelectedReportParticipants(new Set());
    } catch (error) {
      console.error('Erreur lors du téléchargement groupé des rapports:', error);
      toast.dismiss();
      toast.error('Erreur lors de la génération des rapports');
    } finally {
      setBulkReportDownloading(false);
    }
  };

  const deleteAuthUserOnly = async (participant: Participant) => {
    if (!participant.email) {
      toast.error('Ce participant n\'a pas d\'email');
      return;
    }

    if (!confirm(`Supprimer le compte de ${participant.name} (${participant.email}) ?\n\nToutes les traces du compte seront supprimées. Le participant restera dans la liste mais ne pourra plus se connecter. Il pourra se réinscrire avec son code EAN.`)) {
      return;
    }

    try {
      const email = participant.email.toLowerCase().trim();

      const { data: userId, error: userIdError } = await supabase
        .rpc('get_user_id_by_email', { p_email: email });

      if (userIdError) {
        console.error('Error getting user ID:', userIdError);
        toast.error('Erreur lors de la recherche du compte');
        return;
      }

      if (!userId) {
        toast.error('Aucun compte auth trouve pour cet email');
        return;
      }

      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Session invalide');
        return;
      }

      // Clean up all traces in related tables
      await supabase.from('sharing_operation_admins').delete().eq('email', email);
      await supabase.from('simulator_invitations').delete().eq('email', email);
      await supabase.from('simulation_shares').delete().eq('guest_email', email);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user-account`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.session.access_token}`,
          },
          body: JSON.stringify({
            userId: userId,
            userEmail: email,
            participantName: participant.name,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error deleting auth user:', errorData);
        toast.error(`Erreur: ${errorData.error || 'Erreur inconnue'}`);
      } else {
        toast.success(`Compte de "${participant.name}" entièrement supprimé`);

        // Update local participant state
        setParticipants(prev => prev.map(p =>
          p.id === participant.id
            ? { ...p, email: null, account_status: null }
            : p
        ));
      }
    } catch (error) {
      console.error('Error in deleteAuthUserOnly:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    
    try {
      console.log('🚪 Déconnexion admin...');
      
      // Clear all state
      setParticipants([]);
      setLoading(false);
      setShowForm(false);
      setEditingParticipant(null);
      setViewingMemberDashboard(null);
      setRefreshing(false);
      setShowInvoice(false);
      setSelectedParticipantForInvoice(null);
      setShowPeriodSelection(false);
      setSelectedPeriod({ startMonth: '', endMonth: '' });
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Erreur chargement participants:', error);
      }
      
      // Clear session storage
      sessionStorage.clear();
      
      // Force full page reload to ensure clean state
      window.location.href = '/';
      
    } catch (error) {
      console.error('Error logging out:', error);
      // Even if logout fails, clear storage and redirect
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/';
    }
  };

  // Initialise la date de début du filtre volumes à la plus ancienne date d'entrée
  useEffect(() => {
    if (participants.length === 0) return;
    const dates = participants
      .map(p => p.entry_date)
      .filter(Boolean)
      .sort() as string[];
    if (dates.length > 0 && !volumeFilterStart) {
      setVolumeFilterStart(dates[0].slice(0, 7));
    }
  }, [participants]);

  const DEFAULT_BC = {
    accise_ent_t1: 14.21, accise_ent_t2: 12.09, accise_ent_t3: 11.39, accise_ent_t4: 10.69,
    accise_part_t1: 47.48, accise_part_t2: 45.46, accise_part_t3: 44.78, accise_part_t4: 44.11,
    cotisation_energie: 1.9261,
  };

  const combinedTotals = useMemo(() => {
    const bc = billingConfig ?? DEFAULT_BC;
    const filtered = volumeFilterParticipant === 'all'
      ? participants
      : participants.filter(p => p.id === volumeFilterParticipant);

    let volume_complementaire = 0;
    let volume_partage = 0;
    let injection_complementaire = 0;
    let injection_partagee = 0;
    let totalFraisReseau = 0;
    let totalAccisesCotisation = 0;
    let totalEnergiePartagee = 0;
    let fraisDusProducteurs = 0;
    // Période retenue : fin du filtre (ou aujourd'hui si absent)
    const periodEnd = volumeFilterEnd || new Date().toISOString().slice(0, 10);
    const entrepriseFirstEntry = new Map<string, string>();
    const particulierFirstEntry = new Map<string, string>();

    for (const p of filtered) {
      const companyKey = p.company_number ? String(p.company_number) : (p as any).company_name ? String((p as any).company_name).toLowerCase() : p.id;
      if (p.company_number || (p as any).company_name) {
        const entry = String(p.entry_date || '9999-12-31').slice(0, 10);
        const current = entrepriseFirstEntry.get(companyKey);
        if (!current || entry < current) entrepriseFirstEntry.set(companyKey, entry);
      } else {
        particulierFirstEntry.set(p.id, String(p.entry_date || '9999-12-31').slice(0, 10));
      }
      const md = p.monthly_data
        ? (typeof p.monthly_data === 'string' ? JSON.parse(p.monthly_data) : p.monthly_data) as Record<string, any>
        : {};
      const bd = p.billing_data
        ? (typeof p.billing_data === 'string' ? JSON.parse(p.billing_data) : p.billing_data) as Record<string, any>
        : {};

      const annualConsumption = Number(p.annual_consumption || 30);
      const isCompany = !!p.company_number;

      let droitAcciseRate = 0;
      if (isCompany) {
        if (annualConsumption <= 20) droitAcciseRate = bc.accise_ent_t1;
        else if (annualConsumption <= 50) droitAcciseRate = bc.accise_ent_t2;
        else if (annualConsumption <= 1000) droitAcciseRate = bc.accise_ent_t3;
        else droitAcciseRate = bc.accise_ent_t4;
      } else {
        if (annualConsumption <= 20) droitAcciseRate = bc.accise_part_t1;
        else if (annualConsumption <= 50) droitAcciseRate = bc.accise_part_t2;
        else if (annualConsumption <= 1000) droitAcciseRate = bc.accise_part_t3;
        else droitAcciseRate = bc.accise_part_t4;
      }

      for (const [monthKey, data] of Object.entries(md)) {
        if (volumeFilterStart && monthKey < volumeFilterStart) continue;
        if (volumeFilterEnd && monthKey > volumeFilterEnd) continue;

        const vp = Number((data as any).volume_partage || 0);
        const vc = Number((data as any).volume_complementaire || 0);
        const ip = Number((data as any).injection_partagee || 0);
        const ic = Number((data as any).injection_complementaire || 0);
        volume_partage += vp;
        volume_complementaire += vc;
        injection_partagee += ip;
        injection_complementaire += ic;

        const sharedMWh = vp / 1000;
        totalFraisReseau += Number(bd[monthKey]?.networkCosts?.totalFraisReseau || 0);
        totalAccisesCotisation += sharedMWh * (droitAcciseRate + Number(bc.cotisation_energie));

        // a) Énergie partagée = volume des consommateurs et prosumers × leur prix d'achat
        // b) Frais dus aux producteurs = injection partagée des producteurs et prosumers × leur prix de vente
        const pOverrides = tariffOverridesMap[p.id] || [];
        const isProsumer = (p as any).type === 'prosumer';
        const isProducer = (p as any).type === 'producer';
        const isBuyer = (p as any).type === 'consumer' || isProsumer;
        const variableFormula = (p as any).shared_energy_formula_type === 'variable' && (p as any).shared_energy_a && (p as any).shared_energy_x_param;
        const a = parseFloat((p as any).shared_energy_a) || 0;

        let buyPrice: number;
        if (variableFormula) {
          const xVal = allIdxValues[(p as any).shared_energy_x_param]?.[monthKey];
          buyPrice = xVal !== undefined ? a * xVal : Number(p.shared_energy_price || 80);
        } else if (isProsumer && (p as any).prosumer_buy_price != null) {
          buyPrice = resolvePriceForMonth((p as any).prosumer_buy_price, pOverrides.filter(o => o.price_type === 'prosumer_buy_price'), monthKey);
        } else {
          buyPrice = resolvePriceForMonth(p.shared_energy_price ?? null, pOverrides.filter(o => o.price_type === 'shared_energy_price'), monthKey);
        }
        if (isBuyer) {
          totalEnergiePartagee += sharedMWh * buyPrice;
        }

        if (isProducer || isProsumer) {
          let sellPrice: number;
          if (isProsumer && (p as any).prosumer_sell_price != null) {
            sellPrice = resolvePriceForMonth((p as any).prosumer_sell_price, pOverrides.filter(o => o.price_type === 'prosumer_sell_price'), monthKey);
          } else if (variableFormula) {
            const xVal = allIdxValues[(p as any).shared_energy_x_param]?.[monthKey];
            sellPrice = xVal !== undefined ? a * xVal : Number(p.shared_energy_price || 80);
          } else {
            sellPrice = resolvePriceForMonth(p.shared_energy_price ?? null, pOverrides.filter(o => o.price_type === 'shared_energy_price'), monthKey);
          }
          fraisDusProducteurs += (ip / 1000) * sellPrice;
        }
      }
    }

    return {
      volume_complementaire,
      volume_partage,
      injection_complementaire,
      injection_partagee,
      totalFraisReseau,
      totalAccisesCotisation,
      totalEnergiePartagee,
      fraisDusProducteurs,
      nbEntreprises: [...entrepriseFirstEntry.entries()].filter(([, entry]) => entry <= periodEnd).length,
      nbParticuliers: [...particulierFirstEntry.entries()].filter(([, entry]) => entry <= periodEnd).length,
    };
  }, [participants, volumeFilterStart, volumeFilterEnd, volumeFilterParticipant, billingConfig, allIdxValues, tariffOverridesMap]);

  // Hook de déconnexion automatique
  useAutoLogout({
    onLogout: handleLogout,
    timeoutMinutes: 15,
    isLoggedIn: true
  });

  if (showForm) {
    return (
      <div className="min-h-screen bg-white">
        <header className="bg-white shadow-sm border-b border-neutral-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <button
                  onClick={() => setShowForm(false)}
                  className="mr-4 p-2 text-neutral-600 hover:text-neutral-900"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <h1 className="text-2xl font-bold text-neutral-900">
                  {editingParticipant ? 'Modifier le participant' : 'Ajouter un participant'}
                </h1>
              </div>
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="inline-flex items-center px-4 py-2 border border-brand-flame text-sm font-medium rounded-md shadow-sm text-brand-flame bg-white hover:bg-brand-flame hover:text-white disabled:bg-gray-400 disabled:text-white disabled:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-flame transition-colors"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {isLoggingOut ? 'Déconnexion...' : 'Déconnexion'}
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-xl shadow-lg border border-neutral-300 p-8">
            <ParticipantForm
              participant={editingParticipant}
              onSuccess={handleFormSuccess}
              onCancel={() => setShowForm(false)}
              sharingOperationId={sharingOperationId!}
            />
          </div>
        </main>
      </div>
    );
  }

  if (viewingMemberDashboard) {
    return (
      <MemberDashboard
        user={viewingMemberDashboard}
        onLogout={handleCloseMemberDashboard}
        isAdminView={true}
        canViewQh={can('donnees_qh')}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  // Group participants by groupe for display
  const groupedParticipants = new Map<string, Participant[]>();
  participants.forEach(p => {
    if (p.groupe) {
      if (!groupedParticipants.has(p.groupe)) {
        groupedParticipants.set(p.groupe, []);
      }
      groupedParticipants.get(p.groupe)!.push(p);
    }
  });

  // Sort participants
  const sortedParticipants = [...participants].sort((a, b) => {
    const [field, order] = sortBy.includes('-') ? sortBy.split('-') : [sortBy, sortOrder];

    let aValue: any;
    let bValue: any;

    switch (field) {
      case 'name':
        aValue = a.name?.toLowerCase() || '';
        bValue = b.name?.toLowerCase() || '';
        break;
      case 'type':
        aValue = a.type || '';
        bValue = b.type || '';
        break;
      case 'entry_date':
        aValue = a.entry_date || '';
        bValue = b.entry_date || '';
        break;
      case 'company_name':
        aValue = a.company_name?.toLowerCase() || '';
        bValue = b.company_name?.toLowerCase() || '';
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });

  const loadSavedLinks = async () => {
    const { data, error } = await supabase
      .from('monthly_csv_links')
      .select('*')
      .order('year', { ascending: false })
      .order('month_number', { ascending: false })
      .order('created_at', { ascending: true });
    if (!error && data) setSavedLinks(data);
  };

  const handleSaveNewLink = async () => {
    if (!newLinkUrl || !newLinkMonth) return;
    setSavingLink(true);
    try {
      const [year, month] = newLinkMonth.split('-').map(Number);
      const monthNames = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
      const monthName = monthNames[month - 1];
      const converted = convertGoogleSheetUrl(newLinkUrl.trim());
      const { error } = await supabase.from('monthly_csv_links').insert({
        csv_url: converted,
        month_name: monthName,
        year,
        month_number: month,
        label: newLinkLabel.trim(),
        sharing_operation_id: sharingOperationId ?? null,
      });
      if (error) throw error;
      toast.success('Lien enregistré');
      setNewLinkUrl('');
      setNewLinkLabel('');
      setNewLinkMonth('');
      await loadSavedLinks();
    } catch (err: any) {
      toast.error('Erreur lors de l\'enregistrement : ' + (err.message || err));
    } finally {
      setSavingLink(false);
    }
  };

  const handleRenameLink = async (id: string) => {
    const { error } = await supabase
      .from('monthly_csv_links')
      .update({ label: renamingLinkValue.trim() })
      .eq('id', id);
    if (error) {
      toast.error('Erreur renommage');
    } else {
      setSavedLinks(prev => prev.map(l => l.id === id ? { ...l, label: renamingLinkValue.trim() } : l));
      toast.success('Lien renommé');
    }
    setRenamingLinkId(null);
    setRenamingLinkValue('');
  };

  const handleDeleteLink = async (id: string) => {
    if (!confirm('Supprimer ce lien ?')) return;
    const { error } = await supabase.from('monthly_csv_links').delete().eq('id', id);
    if (error) {
      toast.error('Erreur suppression');
    } else {
      setSavedLinks(prev => prev.filter(l => l.id !== id));
      const newSelected = new Set(selectedMonths);
      newSelected.delete(id);
      setSelectedMonths(newSelected);
      toast.success('Lien supprimé');
    }
  };

  const renderSharingDataTab = () => (
    <>
      {/* Volumes & frais totaux */}
      <div className="bg-white rounded-xl shadow-sm border border-neutral-200 mb-6 overflow-hidden">
        {/* Header + filtres */}
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-5 h-5 text-amber-500" />
            <h3 className="font-semibold text-neutral-900">Volumes &amp; frais totaux</h3>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={volumeFilterParticipant}
              onChange={e => setVolumeFilterParticipant(e.target.value)}
              className="border border-neutral-300 rounded-lg px-3 py-1.5 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
            >
              <option value="all">Tous les participants</option>
              {participants.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-neutral-500">Du</label>
              <input
                type="month"
                value={volumeFilterStart}
                onChange={e => setVolumeFilterStart(e.target.value)}
                className="border border-neutral-300 rounded-lg px-3 py-1.5 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-neutral-500">Au</label>
              <input
                type="month"
                value={volumeFilterEnd}
                onChange={e => setVolumeFilterEnd(e.target.value)}
                className="border border-neutral-300 rounded-lg px-3 py-1.5 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
          </div>
        </div>

        {/* Volumes */}
        <div className="px-6 pt-4 pb-1">
          <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-2">Volumes</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-neutral-200 border-b border-neutral-200">
          <div className="px-6 py-4">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">Conso. réseau</p>
            <p className="text-2xl font-bold text-neutral-800">
              {(combinedTotals.volume_complementaire / 1000).toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-neutral-400 mt-0.5">MWh</p>
          </div>
          <div className="px-6 py-4">
            <p className="text-xs font-medium text-amber-600 uppercase tracking-wide mb-1">Conso. partagée</p>
            <p className="text-2xl font-bold text-amber-700">
              {(combinedTotals.volume_partage / 1000).toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-amber-400 mt-0.5">MWh</p>
          </div>
          <div className="px-6 py-4">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">Injection réseau</p>
            <p className="text-2xl font-bold text-neutral-800">
              {(combinedTotals.injection_complementaire / 1000).toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-neutral-400 mt-0.5">MWh</p>
          </div>
          <div className="px-6 py-4">
            <p className="text-xs font-medium text-teal-600 uppercase tracking-wide mb-1">Injection partagée</p>
            <p className="text-2xl font-bold text-teal-700">
              {(combinedTotals.injection_partagee / 1000).toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-teal-400 mt-0.5">MWh</p>
          </div>
        </div>

        {/* Frais */}
        <div className="px-6 pt-4 pb-1">
          <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-2">Frais</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-y md:divide-y-0 divide-neutral-200">
          <div className="px-6 py-4">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">Frais réseau</p>
            <p className="text-2xl font-bold text-neutral-800">
              {combinedTotals.totalFraisReseau.toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-neutral-400 mt-0.5">€ <span className="font-semibold text-neutral-500">HTVA</span></p>
          </div>
          <div className="px-6 py-4">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">Accises &amp; cotisation</p>
            <p className="text-2xl font-bold text-neutral-800">
              {combinedTotals.totalAccisesCotisation.toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-neutral-400 mt-0.5">€ <span className="font-semibold text-neutral-500">HTVA</span></p>
          </div>
          <div className="px-6 py-4">
            <p className="text-xs font-medium text-amber-600 uppercase tracking-wide mb-1">Énergie partagée</p>
            <p className="text-2xl font-bold text-amber-700">
              {combinedTotals.totalEnergiePartagee.toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-amber-400 mt-0.5">€ <span className="font-semibold text-amber-500">HTVA</span></p>
            <p className="text-[11px] leading-snug text-neutral-500 mt-2">dont frais dûs aux producteurs : {combinedTotals.fraisDusProducteurs.toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € HTVA</p>
          </div>
          <div className="px-6 py-4">
            <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1">Entreprises</p>
            <p className="text-2xl font-bold text-emerald-700">{combinedTotals.nbEntreprises}</p>
            <p className="text-xs text-emerald-500 mt-0.5">distinctes</p>
          </div>
          <div className="px-6 py-4">
            <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1">Particuliers</p>
            <p className="text-2xl font-bold text-emerald-700">{combinedTotals.nbParticuliers}</p>
            <p className="text-xs text-emerald-500 mt-0.5">distincts</p>
          </div>
        </div>
      </div>

      <MonthlyFileManager onImportSuccess={handleRefreshData} sharingOperationId={sharingOperationId} canImport={can('import_data')} />
      {can('donnees_qh') && <div className="mt-10">

        {/* Section liens Google Sheet */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 mb-6 overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold text-neutral-900">Liens Google Sheet (données quart-horaires)</h3>
            </div>
            <button
              onClick={async () => {
                if (!showSavedLinks) await loadSavedLinks();
                setShowSavedLinks(!showSavedLinks);
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors border border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <BarChart3 className="w-4 h-4" />
              {showSavedLinks ? 'Masquer' : `Voir les liens enregistrés`}
            </button>
          </div>

          {/* Formulaire ajout nouveau lien */}
          <div className="px-6 py-5 bg-neutral-50 border-b border-neutral-200">
            <p className="text-sm font-medium text-neutral-700 mb-3">Ajouter un nouveau lien</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Mois</label>
                <input
                  type="month"
                  value={newLinkMonth}
                  onChange={(e) => setNewLinkMonth(e.target.value)}
                  className="border border-neutral-300 rounded-lg px-3 py-2 w-full text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Nom du lien (optionnel)</label>
                <input
                  type="text"
                  value={newLinkLabel}
                  onChange={(e) => setNewLinkLabel(e.target.value)}
                  placeholder="ex: Consommateurs, Producteurs…"
                  className="border border-neutral-300 rounded-lg px-3 py-2 w-full text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              <div>
                <label className="block text-xs text-neutral-500 mb-1">URL Google Sheet</label>
                <input
                  type="text"
                  value={newLinkUrl}
                  onChange={(e) => setNewLinkUrl(e.target.value)}
                  placeholder="Collez le lien Google Sheet ici"
                  className="border border-neutral-300 rounded-lg px-3 py-2 w-full text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSaveNewLink}
                disabled={!newLinkUrl || !newLinkMonth || savingLink}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {savingLink ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Enregistrer le lien
              </button>
              {newLinkUrl && (
                <p className="text-xs text-green-600">Lien sera converti automatiquement en format CSV</p>
              )}
            </div>
          </div>

          {/* Liste des liens enregistrés */}
          {showSavedLinks && (
            <div className="px-6 py-5">
              {savedLinks.length === 0 ? (
                <p className="text-sm text-neutral-500 py-2">Aucun lien enregistré pour le moment.</p>
              ) : (
                <>
                  <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                    {savedLinks.map((link) => (
                      <div
                        key={link.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                          selectedMonths.has(link.id)
                            ? 'border-amber-400 bg-amber-50'
                            : 'border-neutral-200 bg-white hover:bg-neutral-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedMonths.has(link.id)}
                          onChange={(e) => {
                            const newSelected = new Set(selectedMonths);
                            if (e.target.checked) newSelected.add(link.id);
                            else newSelected.delete(link.id);
                            setSelectedMonths(newSelected);
                          }}
                          className="w-4 h-4 accent-amber-500 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          {renamingLinkId === link.id ? (
                            <div className="flex items-center gap-2">
                              <input
                                autoFocus
                                type="text"
                                value={renamingLinkValue}
                                onChange={(e) => setRenamingLinkValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleRenameLink(link.id);
                                  if (e.key === 'Escape') { setRenamingLinkId(null); setRenamingLinkValue(''); }
                                }}
                                className="border border-amber-400 rounded-md px-2 py-1 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-amber-400"
                              />
                              <button onClick={() => handleRenameLink(link.id)} className="text-xs text-amber-600 hover:text-amber-800 font-medium">Valider</button>
                              <button onClick={() => { setRenamingLinkId(null); setRenamingLinkValue(''); }} className="text-xs text-neutral-500 hover:text-neutral-700">Annuler</button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-neutral-900 text-sm">{link.month_name} {link.year}</span>
                              {link.label ? (
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">{link.label}</span>
                              ) : null}
                              <span className="text-xs text-neutral-400 truncate max-w-xs hidden md:block">{link.csv_url}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => {
                              setRenamingLinkId(link.id);
                              setRenamingLinkValue(link.label || '');
                            }}
                            title="Renommer"
                            className="p-1.5 rounded-md text-neutral-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              const newSelected = new Set([link.id]);
                              setSelectedMonths(newSelected);
                              setCsvUrl(link.csv_url);
                            }}
                            title="Afficher seul"
                            className="p-1.5 rounded-md text-neutral-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteLink(link.id)}
                            title="Supprimer"
                            className="p-1.5 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {selectedMonths.size > 0 && (
                    <button
                      onClick={() => {
                        const selectedLinks = savedLinks.filter(link => selectedMonths.has(link.id));
                        const urls = selectedLinks.map(l => l.csv_url).join(',');
                        setCsvUrl(urls);
                      }}
                      className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                    >
                      <BarChart3 className="w-4 h-4" />
                      Afficher {selectedMonths.size} lien{selectedMonths.size > 1 ? 's' : ''} sélectionné{selectedMonths.size > 1 ? 's' : ''}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Saisie manuelle URL */}
        <div className="bg-neutral-50 rounded-xl border border-neutral-200 px-6 py-4 mb-6">
          <label className="block text-sm font-medium text-neutral-700 mb-2">Ou saisir directement une URL (non enregistrée)</label>
          <input
            type="text"
            value={csvUrl}
            onChange={(e) => {
              const input = e.target.value.trim();
              const converted = convertGoogleSheetUrl(input);
              setCsvUrl(converted);
            }}
            placeholder="Collez ici le lien Google Sheet (édition ou CSV)"
            className="border border-neutral-300 rounded-lg p-2 w-full text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
          {csvUrl && (
            <p className="text-xs text-green-600 mt-1">Lien CSV detecte automatiquement</p>
          )}
        </div>

        {csvUrl && <AdminEanChart csvUrl={csvUrl} />}
      </div>}
    </>
  );

  const renderParticipantsTab = () => (
        <div className="bg-white rounded-xl shadow-lg border border-neutral-300">
          <div className="px-6 py-4 border-b border-neutral-300">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center">
                <Users className="w-6 h-6 text-amber-600 mr-3" />
                <div>
                  <h2 className="text-xl font-semibold text-neutral-900">Participants de la communauté</h2>
                  <p className="text-sm text-neutral-600 mt-1">
                    {participants.length} participant(s) - {participants.filter(p => p.ean_code).length} avec code EAN
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-3">
                  {(can('generate_invoice') || can('generate_report')) && (
                    <div className="relative">
                      <button
                        onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                        className="inline-flex items-center px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Télécharger
                        <svg className={`w-4 h-4 ml-2 transition-transform ${showDownloadMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showDownloadMenu && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-neutral-200 z-20">
                          {can('generate_invoice') && (
                            <button
                              onClick={() => {
                                setShowBulkDownloadModal(true);
                                setShowDownloadMenu(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-neutral-50 flex items-center space-x-2 rounded-t-lg"
                            >
                              <FileText className="w-4 h-4 text-brand-teal" />
                              <span className="text-sm text-neutral-700">Factures</span>
                            </button>
                          )}
                          {can('generate_report') && (
                            <button
                              onClick={() => {
                                setShowBulkReportModal(true);
                                setShowDownloadMenu(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-neutral-50 flex items-center space-x-2 rounded-b-lg border-t border-neutral-100"
                            >
                              <BarChart3 className="w-4 h-4 text-amber-600" />
                              <span className="text-sm text-neutral-700">Rapports</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <button
                    onClick={() => setShowInvitationModal(true)}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Invitation d'accès
                  </button>
                  {can('add_member') && (
                    <button
                      onClick={handleAdd}
                      className="inline-flex items-center px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter un participant
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Ligne de filtres */}
            <div className="flex items-center flex-wrap gap-x-4 gap-y-2 mt-3">
              {/* Filtre sous partage */}
              {(() => {
                  const sousPartageValues = [...new Set(participants.map(p => (p as any).sous_partage).filter(Boolean))].sort();
                  return sousPartageValues.length > 0 ? (
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-neutral-600">Sous partage :</span>
                      <select
                        value={filterSousPartage}
                        onChange={(e) => setFilterSousPartage(e.target.value)}
                        className="px-3 py-1 border border-neutral-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      >
                        <option value="">Tous</option>
                        {sousPartageValues.map(val => (
                          <option key={val} value={val}>{val}</option>
                        ))}
                      </select>
                    </div>
                  ) : null;
              })()}

              {/* Recherche par nom ou EAN */}
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher par nom, EAN, entreprise, groupe..."
                    className="pl-8 pr-3 py-1 border border-neutral-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent w-56"
                  />
                </div>
              </div>

              {/* Options de tri */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-neutral-600">Trier par :</span>
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [column, order] = e.target.value.split('-') as [typeof sortBy, typeof sortOrder];
                    setSortBy(column);
                    setSortOrder(order);
                  }}
                  className="px-3 py-1 border border-neutral-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  <option value="name-asc">Nom (A-Z)</option>
                  <option value="name-desc">Nom (Z-A)</option>
                  <option value="company_name-asc">Nom d'entreprise (A-Z)</option>
                  <option value="company_name-desc">Nom d'entreprise (Z-A)</option>
                  <option value="type-asc">Type (Consommateur d'abord)</option>
                  <option value="type-desc">Type (Producteur d'abord)</option>
                  <option value="entry_date-asc">Date d'entrée (Plus ancien)</option>
                  <option value="entry_date-desc">Date d'entrée (Plus récent)</option>
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-white">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    <div className="flex items-center">
                      <User className="w-3 h-3 mr-1" />
                      Nom
                    </div>
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    <div className="flex items-center">
                      <Mail className="w-3 h-3 mr-1" />
                      Compte activé
                    </div>
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    <div className="flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      Date d'entrée
                    </div>
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(() => {
                  const { grouped, ungrouped } = groupParticipants();
                  const hasData = participants.length > 0;
                  
                  if (!hasData) {
                    return (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-neutral-500">
                          <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                          <p className="text-lg font-medium mb-2">Aucun participant enregistré</p>
                          <p className="text-sm">Ajoutez votre premier participant pour commencer</p>
                        </td>
                      </tr>
                    );
                  }

                  const rows: JSX.Element[] = [];

                  // Afficher les groupes
                  Object.entries(grouped).forEach(([groupName, groupParticipants]) => {
                    const isExpanded = expandedGroups.has(groupName);
                    const groupTypes = [...new Set(groupParticipants.map(p => p.type))];
                    const groupTypeDisplay = groupTypes.length === 1
                      ? (groupTypes[0] === 'producer' ? 'Producteurs' : 'Consommateurs')
                      : 'Mixte';
                    const groupTypeColors = groupTypes.length === 1 && groupTypes[0] === 'producer'
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-green-100 text-green-800';

                    // Ligne du groupe
                    rows.push(
                      <tr key={`group-${groupName}`} className="bg-white hover:bg-neutral-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            <button
                              onClick={() => toggleGroup(groupName)}
                              className="flex items-center space-x-2 text-left"
                            >
                              <div className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                                <svg className="w-4 h-4 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Users className="w-4 h-4 text-amber-600" />
                                <div>
                                  <div className="text-xs font-bold text-neutral-900">{groupName}</div>
                                  <div className="text-xs text-neutral-500">{groupParticipants.length} membre(s)</div>
                                </div>
                              </div>
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${groupTypeColors}`}>
                            {groupTypeDisplay}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {(() => {
                            const participantWithEmail = groupParticipants.find(p => p.email);
                            if (!participantWithEmail) {
                              return (
                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                                  <X className="w-3 h-3 mr-1" />
                                  Non active
                                </span>
                              );
                            }
                            const status = getAccountStatus(participantWithEmail);
                            if (status === 'pending') {
                              return (
                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                                  <Clock className="w-3 h-3 mr-1" />
                                  En attente
                                </span>
                              );
                            }
                            if (status === 'approved') {
                              return (
                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Valide
                                </span>
                              );
                            }
                            return (
                              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                <XCircle className="w-3 h-3 mr-1" />
                                Refuse
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-xs text-neutral-500 italic">
                            Voir détails
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs font-medium">
                          <div className="flex items-center space-x-2">
                            {can('generate_invoice') && (
                              <button
                                onClick={() => handleGroupAction('invoice', groupParticipants)}
                                className="text-green-600 hover:text-green-800 transition-colors"
                                title="Générer facture groupe"
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                            )}
                            {can('generate_report') && (
                              <button
                                onClick={() => {
                                  setSelectedGroupForReport({ name: groupName, participants: groupParticipants });
                                  setShowGroupReportModal(true);
                                }}
                                className="text-amber-600 hover:text-amber-700 transition-colors"
                                title="Rapport de groupe"
                              >
                                <BarChart3 className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleGroupAction('dashboard', groupParticipants)}
                              className="text-green-600 hover:text-green-800 transition-colors"
                              title="Voir dashboard groupe"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );

                    // Lignes des membres du groupe (si développé)
                    if (isExpanded) {
                      groupParticipants.forEach(participant => {
                        rows.push(
                          <tr key={participant.id} className="hover:bg-white bg-white">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center pl-8">
                                <div className="text-xs font-medium text-neutral-700">
                                  └ {participant.name}
                                </div>
                                {!participant.ean_code && (
                                  <div className="ml-2">
                                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-neutral-100 text-neutral-500">
                                      <Database className="w-3 h-3 mr-1" />
                                      Non prêt
                                    </span>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                                participant.type === 'producer'
                                  ? 'bg-amber-100 text-amber-800'
                                  : participant.type === 'prosumer'
                                  ? 'bg-orange-100 text-orange-800'
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {participant.type === 'producer' ? 'Producteur' : participant.type === 'prosumer' ? 'Prosumer' : 'Consommateur'}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {(() => {
                                const status = getAccountStatus(participant);
                                if (status === 'none') {
                                  return (
                                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                                      <X className="w-3 h-3 mr-1" />
                                      Non active
                                    </span>
                                  );
                                }
                                if (status === 'pending') {
                                  return (
                                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                                      <Clock className="w-3 h-3 mr-1" />
                                      En attente
                                    </span>
                                  );
                                }
                                if (status === 'approved') {
                                  return (
                                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Valide
                                    </span>
                                  );
                                }
                                return (
                                  <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                    <XCircle className="w-3 h-3 mr-1" />
                                    Refuse
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-xs text-neutral-600">
                                {participant.entry_date ? (
                                  format(new Date(participant.entry_date), 'dd/MM/yyyy', { locale: fr })
                                ) : (
                                  <span className="text-gray-400 italic">Non renseignee</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs font-medium">
                              <div className="flex items-center space-x-2">
                                {can('generate_invoice') && (
                                  <button
                                    onClick={() => handleShowInvoice(participant)}
                                    className="text-green-600 hover:text-green-800 transition-colors"
                                    title="Generer facture individuelle"
                                  >
                                    <FileText className="w-4 h-4" />
                                  </button>
                                )}
                                {can('generate_report') && (
                                  <button
                                    onClick={() => {
                                      setSelectedParticipantForReport(participant);
                                      setShowReportModal(true);
                                    }}
                                    className="text-amber-600 hover:text-amber-700 transition-colors"
                                    title="Generer rapport"
                                  >
                                    <BarChart3 className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    setSelectedParticipantForExport(participant);
                                    setShowExportData(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-700 transition-colors"
                                  title="Exporter données"
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleViewParticipantDashboard(participant)}
                                  className="text-green-600 hover:text-green-800 transition-colors"
                                  title="Voir le dashboard"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                {can('edit_member') && (
                                  <button
                                    onClick={() => handleEdit(participant)}
                                    className="text-neutral-600 hover:text-neutral-900 transition-colors"
                                    title="Modifier"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                )}
                                {can('delete_member') && (
                                  <button
                                    onClick={() => deleteAuthUserOnly(participant)}
                                    className="text-brand-flame hover:text-brand-flame transition-colors"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      });
                    }
                  });

                  // Afficher les participants sans groupe
                  ungrouped.forEach(participant => {
                    rows.push(
                      <tr key={participant.id} className="hover:bg-white">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="text-xs font-medium text-neutral-900">{participant.name}</div>
                            {!participant.ean_code && (
                              <div className="ml-2">
                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-neutral-100 text-neutral-500">
                                  <Database className="w-3 h-3 mr-1" />
                                  Non prêt
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                            participant.type === 'producer'
                              ? 'bg-amber-100 text-amber-800'
                              : participant.type === 'prosumer'
                              ? 'bg-orange-100 text-orange-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {participant.type === 'producer' ? 'Producteur' : participant.type === 'prosumer' ? 'Prosumer' : 'Consommateur'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {(() => {
                            const status = getAccountStatus(participant);
                            if (status === 'none') {
                              return (
                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                                  <X className="w-3 h-3 mr-1" />
                                  Non active
                                </span>
                              );
                            }
                            if (status === 'pending') {
                              return (
                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                                  <Clock className="w-3 h-3 mr-1" />
                                  En attente
                                </span>
                              );
                            }
                            if (status === 'approved') {
                              return (
                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Valide
                                </span>
                              );
                            }
                            return (
                              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                <XCircle className="w-3 h-3 mr-1" />
                                Refuse
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-xs text-neutral-900">
                            {participant.entry_date ? (
                              format(new Date(participant.entry_date), 'dd/MM/yyyy', { locale: fr })
                            ) : (
                              <span className="text-gray-400 italic">Non renseignee</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs font-medium">
                          <div className="flex items-center space-x-2">
                            {can('generate_invoice') && (
                              <button
                                onClick={() => handleShowInvoice(participant)}
                                className="text-green-600 hover:text-green-800 transition-colors"
                                title="Générer facture"
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                            )}
                            {can('generate_report') && (
                              <button
                                onClick={() => {
                                  setSelectedParticipantForReport(participant);
                                  setShowReportModal(true);
                                }}
                                className="text-amber-600 hover:text-amber-700 transition-colors"
                                title="Générer rapport"
                              >
                                <BarChart3 className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setSelectedParticipantForExport(participant);
                                setShowExportData(true);
                              }}
                              className="text-blue-600 hover:text-blue-700 transition-colors"
                              title="Exporter données"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleViewParticipantDashboard(participant)}
                              className="text-green-600 hover:text-green-800 transition-colors"
                              title="Voir le dashboard"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {can('edit_member') && (
                              <button
                                onClick={() => handleEdit(participant)}
                                className="text-neutral-600 hover:text-neutral-900 transition-colors"
                                title="Modifier"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            )}
                            {can('delete_member') && (
                              <button
                                onClick={() => deleteAuthUserOnly(participant)}
                                className="text-brand-flame hover:text-brand-flame transition-colors"
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                            </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  });

                  return rows;
                })()}
              </tbody>
            </table>
          </div>
        </div>
  );

  return (
    <>
      <div className="min-h-screen bg-white">
        <header className="bg-white shadow-sm border-b border-neutral-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <button
                  onClick={() => window.location.href = '/admin/operations'}
                  className="mr-4 p-2 hover:bg-neutral-100 rounded-lg transition-colors"
                  title="Retour aux opérations"
                >
                  <ArrowLeft className="w-5 h-5 text-neutral-600" />
                </button>
                <div className="flex-shrink-0">
                  <img src="/images/Logosite2.png" alt="Sun Is Up Logo" className="h-[53px] w-[53px]" />
                </div>
                <div className="ml-4">
                  <h1 className="text-2xl font-bold text-neutral-900">
                    {sharingOperationName || 'Tableau de bord administrateur'}
                  </h1>
                  <p className="text-sm text-neutral-600">
                    Gestion des participants
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleRefreshData}
                  disabled={refreshing}
                  className="inline-flex items-center px-3 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Actualiser les donnees"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Actualisation...' : 'Actualiser'}
                </button>

                {dualRoleServices.includes('simulator') && (
                  <button
                    onClick={() => { localStorage.removeItem('dual_role_services'); window.location.href = '/'; }}
                    className="inline-flex items-center px-3 py-2 border border-[#F8BD3D] text-sm font-medium rounded-md text-neutral-800 bg-[#FFF9EC] hover:bg-[#FFF3D0] transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    Changer de service
                  </button>
                )}

                <button
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="inline-flex items-center px-4 py-2 border border-brand-flame text-sm font-medium rounded-md shadow-sm text-brand-flame bg-white hover:bg-brand-flame hover:text-white disabled:bg-gray-400 disabled:text-white disabled:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-flame transition-colors"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {isLoggingOut ? 'Déconnexion...' : 'Déconnexion'}
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="flex">
          <div className="fixed left-0 top-1/2 -translate-y-1/2 z-40">
            <div className="bg-white shadow-lg rounded-r-xl border border-l-0 border-neutral-200 overflow-hidden">
              <div className="flex flex-col py-2">
                <button
                  onClick={() => setActiveTab('sharing-data')}
                  className={`flex items-center justify-center w-14 h-14 transition-colors ${
                    activeTab === 'sharing-data' ? 'bg-amber-50 text-amber-600' : 'text-neutral-600 hover:bg-neutral-50'
                  }`}
                  title="Donnees de partage"
                >
                  <Share2 className="w-6 h-6" />
                </button>

                <button
                  onClick={() => setActiveTab('participants')}
                  className={`flex items-center justify-center w-14 h-14 transition-colors ${
                    activeTab === 'participants' ? 'bg-amber-50 text-amber-600' : 'text-neutral-600 hover:bg-neutral-50'
                  }`}
                  title="Participants et factures"
                >
                  <Users className="w-6 h-6" />
                </button>

              </div>
            </div>
          </div>

          <main className="flex-1 ml-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {activeTab === 'sharing-data' && renderSharingDataTab()}
            {activeTab === 'participants' && renderParticipantsTab()}
          </main>
        </div>
      </div>

      {/* Modal de selection de periode */}
      {showPeriodSelection && selectedParticipantForInvoice && (
        <>
          {console.log('🖼️ RENDU MODAL PÉRIODE:', { 
            showPeriodSelection, 
            selectedParticipantForInvoice: selectedParticipantForInvoice?.name 
          })}
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <Calendar className="w-6 h-6 text-amber-600" />
                  <h3 className="text-xl font-semibold text-neutral-900">
                    Période de facturation
                  </h3>
                </div>
                <button
                  onClick={() => setShowPeriodSelection(false)}
                  className="text-neutral-500 hover:text-neutral-700 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    {selectedParticipantForInvoice.groupe ? (
                      <Users className="w-4 h-4 text-brand-teal" />
                    ) : (
                      <User className="w-4 h-4 text-brand-teal" />
                    )}
                    <span className="font-medium text-brand-teal">
                      {selectedParticipantForInvoice.groupe
                        ? `Groupe ${selectedParticipantForInvoice.groupe}`
                        : selectedParticipantForInvoice.name}
                    </span>
                  </div>
                  <p className="text-sm text-brand-teal">
                    Sélectionnez la période pour laquelle générer la facture
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Mois de début
                    </label>
                    <input
                      type="month"
                      value={selectedPeriod.startMonth}
                      onChange={(e) => setSelectedPeriod(prev => ({ ...prev, startMonth: e.target.value }))}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Mois de fin
                    </label>
                    <input
                      type="month"
                      value={selectedPeriod.endMonth}
                      onChange={(e) => setSelectedPeriod(prev => ({ ...prev, endMonth: e.target.value }))}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>

                  {/* Info frais d'adhésion - automatiques selon mois anniversaire */}
                  {selectedParticipantForInvoice.groupe && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <Euro className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-amber-900">Frais d'adhésion automatiques</p>
                          <p className="text-xs text-amber-700 mt-1">
                            Les frais d'adhésion sont inclus automatiquement si la période couvre le mois anniversaire d'entrée du groupe (basé sur la date d'entrée la plus ancienne).
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Checkbox pour régularisation annuelle des accises */}
                  {selectedParticipantForInvoice.type !== 'producer' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isAnnualRegularization}
                          onChange={(e) => setIsAnnualRegularization(e.target.checked)}
                          className="w-4 h-4 text-brand-teal border-blue-300 rounded focus:ring-brand-teal"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <RotateCcw className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-blue-900">
                              Régularisation annuelle des accises
                            </span>
                          </div>
                          <p className="text-xs text-blue-700 mt-1">
                            Calcule les accises sur base des volumes partagés annuels au lieu de la période sélectionnée
                          </p>
                        </div>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowPeriodSelection(false)}
                  className="px-4 py-2 text-neutral-700 hover:text-neutral-900 transition-colors"
                >
                  Annuler
                </button>
                {selectedParticipantForInvoice.groupe && (
                  <button
                    onClick={handlePeriodConfirmByCompany}
                    disabled={loadingCompanySelector}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
                  >
                    {loadingCompanySelector ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white" />
                    ) : (
                      <Building2 className="w-4 h-4" />
                    )}
                    <span>Par entreprise</span>
                  </button>
                )}
                <button
                  onClick={handlePeriodConfirm}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                >
                  <FileText className="w-4 h-4" />
                  <span>Générer la facture</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Modal de sélection d'entreprise pour facture individuelle */}
      {showCompanySelector && selectedParticipantForInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <Building2 className="w-6 h-6 text-teal-600" />
                  <h3 className="text-xl font-semibold text-neutral-900">Sélectionner les entreprises</h3>
                </div>
                <button
                  onClick={() => { setShowCompanySelector(false); handleCloseInvoice(); }}
                  className="text-neutral-500 hover:text-neutral-700 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mb-4">
                <p className="text-sm font-medium text-teal-800">
                  Groupe {selectedParticipantForInvoice.groupe}
                </p>
                <p className="text-xs text-teal-600 mt-0.5">
                  Période : {format(parseISO(selectedPeriod.startMonth + '-01'), 'MMMM yyyy', { locale: fr })}
                  {selectedPeriod.startMonth !== selectedPeriod.endMonth &&
                    ` → ${format(parseISO(selectedPeriod.endMonth + '-01'), 'MMMM yyyy', { locale: fr })}`}
                </p>
              </div>

              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  {selectedCompanyIndices.size} / {companySelectorItems.length} sélectionnée(s)
                </p>
                <button
                  onClick={() => {
                    if (selectedCompanyIndices.size === companySelectorItems.length) {
                      setSelectedCompanyIndices(new Set());
                    } else {
                      setSelectedCompanyIndices(new Set(companySelectorItems.map((_, i) => i)));
                    }
                  }}
                  className="text-xs text-teal-600 hover:text-teal-800 font-medium transition-colors"
                >
                  {selectedCompanyIndices.size === companySelectorItems.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                </button>
              </div>

              <div className="space-y-2">
                {companySelectorItems.map((company, idx) => (
                  <label
                    key={idx}
                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedCompanyIndices.has(idx)
                        ? 'border-teal-400 bg-teal-50'
                        : 'border-neutral-200 hover:bg-neutral-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCompanyIndices.has(idx)}
                      onChange={() => {
                        setSelectedCompanyIndices(prev => {
                          const next = new Set(prev);
                          if (next.has(idx)) next.delete(idx);
                          else next.add(idx);
                          return next;
                        });
                      }}
                      className="w-4 h-4 text-teal-600 border-neutral-300 rounded focus:ring-teal-500 mr-3 flex-shrink-0"
                    />
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-4 h-4 text-teal-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-neutral-900 truncate">
                          {company.company_name || (company.company_number ? `N° ${company.company_number}` : "Sans numéro d'entreprise")}
                        </p>
                        {company.company_number && company.company_name && (
                          <p className="text-xs font-mono text-neutral-400">{company.company_number}</p>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {(selectedParticipantForInvoice.type === 'producer' || selectedParticipantForInvoice.type === 'prosumer') && (
                <div className="mt-3 flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Ban className="w-4 h-4 text-red-500" />
                    <span className="text-sm font-medium text-red-800">Exclure la cotisation annuelle</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setBulkExcludeMembershipFee(v => !v)}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${bulkExcludeMembershipFee ? 'bg-red-500' : 'bg-neutral-300'}`}
                  >
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${bulkExcludeMembershipFee ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                </div>
              )}

              {(selectedParticipantForInvoice.type === 'producer' || selectedParticipantForInvoice.type === 'prosumer') && (
                <div className="mt-4 flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Euro className="w-4 h-4 text-amber-600" />
                    <div>
                      <span className="text-sm font-medium text-amber-800">Forcer l'inclusion de la cotisation annuelle</span>
                      <p className="text-xs text-amber-600 mt-0.5">Par défaut, la cotisation est ajoutée automatiquement si la période couvre le mois anniversaire du groupe.</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setBulkIncludeMembershipFee(v => !v)}
                    disabled={bulkExcludeMembershipFee}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${bulkIncludeMembershipFee && !bulkExcludeMembershipFee ? 'bg-amber-500' : 'bg-neutral-300'} ${bulkExcludeMembershipFee ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${bulkIncludeMembershipFee && !bulkExcludeMembershipFee ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                </div>
              )}

              <div className="mt-5 flex items-center justify-between">
                <button
                  onClick={() => { setShowCompanySelector(false); setShowPeriodSelection(true); }}
                  className="px-4 py-2 text-neutral-600 hover:text-neutral-900 transition-colors text-sm flex items-center space-x-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Retour</span>
                </button>
                <button
                  onClick={handleGenerateSelectedCompanies}
                  disabled={selectedCompanyIndices.size === 0}
                  className="flex items-center space-x-2 px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  <span>
                    {selectedCompanyIndices.size <= 1
                      ? 'Générer la facture'
                      : `Générer ${selectedCompanyIndices.size} factures`}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de téléchargement groupé des factures */}
      {showBulkDownloadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-neutral-200">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold text-neutral-900 flex items-center space-x-2">
                    <Download className="w-6 h-6 text-brand-teal" />
                    <span>Télécharger les factures</span>
                  </h3>
                  <p className="text-sm text-neutral-600 mt-1">
                    Sélectionnez les participants et la période de facturation
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowBulkDownloadModal(false);
                    setSelectedParticipants(new Set());
                  }}
                  className="text-neutral-500 hover:text-neutral-700 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Participant selection */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-sm font-medium text-neutral-700">
                      Participants ({selectedParticipants.size} sélectionné{selectedParticipants.size > 1 ? 's' : ''})
                    </label>
                    <button
                      onClick={selectAllParticipants}
                      className="text-sm text-brand-teal hover:text-brand-teal/80 font-medium"
                    >
                      {selectedParticipants.size === participants.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                    </button>
                  </div>
                  <div className="border border-neutral-300 rounded-lg max-h-64 overflow-y-auto">
                    {sortedParticipants.map((participant) => {
                      const isInGroup = participant.groupe && groupedParticipants.has(participant.groupe);
                      const group = isInGroup ? groupedParticipants.get(participant.groupe)! : null;
                      const isFirstInGroup = group && group[0].id === participant.id;

                      // Only show first participant in group or individual participants
                      if (isInGroup && !isFirstInGroup) return null;

                      // Pour les groupes, trouver la date d'entrée la plus ancienne
                      const displayDate = isInGroup && group
                        ? group
                            .map(p => p.entry_date)
                            .filter(date => date !== null)
                            .sort((a, b) => new Date(a!).getTime() - new Date(b!).getTime())[0]
                        : participant.entry_date;

                      return (
                        <label
                          key={participant.id}
                          className="flex items-center p-3 hover:bg-neutral-50 cursor-pointer border-b border-neutral-200 last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            checked={selectedParticipants.has(participant.id)}
                            onChange={() => toggleParticipantSelection(participant.id)}
                            className="w-4 h-4 text-brand-teal border-neutral-300 rounded focus:ring-brand-teal"
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                {isInGroup && <Users className="w-4 h-4 text-brand-teal" />}
                                <span className="font-medium text-neutral-900">
                                  {isInGroup ? `Groupe ${participant.groupe}` : participant.name}
                                </span>
                                <span className={`px-2 py-0.5 text-xs rounded-full ${
                                  participant.type === 'producer'
                                    ? 'bg-amber-100 text-amber-800'
                                    : participant.type === 'prosumer'
                                    ? 'bg-orange-100 text-orange-800'
                                    : 'bg-teal-100 text-teal-800'
                                }`}>
                                  {participant.type === 'producer' ? 'Producteur' : participant.type === 'prosumer' ? 'Prosumer' : 'Consommateur'}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2 text-xs text-neutral-600">
                                <Calendar className="w-3 h-3" />
                                <span>
                                  {displayDate ? (
                                    format(new Date(displayDate), 'dd/MM/yyyy', { locale: fr })
                                  ) : (
                                    <span className="text-gray-400 italic">Non renseignée</span>
                                  )}
                                </span>
                              </div>
                            </div>
                            {isInGroup && (
                              <div className="text-sm text-neutral-600 mt-1">
                                {group!.length} membre{group!.length > 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Period selection */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Mois de début
                    </label>
                    <input
                      type="month"
                      value={bulkPeriod.startMonth}
                      onChange={(e) => setBulkPeriod(prev => ({ ...prev, startMonth: e.target.value }))}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Mois de fin
                    </label>
                    <input
                      type="month"
                      value={bulkPeriod.endMonth}
                      onChange={(e) => setBulkPeriod(prev => ({ ...prev, endMonth: e.target.value }))}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <p className="text-sm text-brand-teal">
                    Un fichier ZIP sera généré contenant une facture PDF pour chaque participant ou groupe sélectionné.
                  </p>
                </div>

                <div className="border border-neutral-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Ban className="w-4 h-4 text-red-500" />
                      <span className="text-sm font-medium text-neutral-800">Exclure les frais d'adhésion</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setBulkExcludeMembershipFee(v => !v)}
                      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${bulkExcludeMembershipFee ? 'bg-red-500' : 'bg-neutral-300'}`}
                    >
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${bulkExcludeMembershipFee ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  {bulkExcludeMembershipFee ? (
                    <p className="text-xs text-red-600 pl-6">Les frais d'adhésion seront exclus de toutes les factures, même s'il s'agit de la première facture annuelle du groupe.</p>
                  ) : (
                    <p className="text-xs text-neutral-500 pl-6">Non cochée par défaut. Les frais d'adhésion sont inclus automatiquement si la période couvre le mois anniversaire du groupe.</p>
                  )}
                </div>

                <div className="border border-neutral-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-medium text-neutral-800">Fixer la date d'émission sur la facture du membre</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setBulkFixIssueDate(v => !v)}
                      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${bulkFixIssueDate ? 'bg-amber-500' : 'bg-neutral-300'}`}
                    >
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${bulkFixIssueDate ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  {bulkFixIssueDate && (
                    <div className="pl-6">
                      <label className="block text-xs font-medium text-neutral-600 mb-1">Date d'émission</label>
                      <input
                        type="date"
                        value={bulkIssueDate}
                        onChange={(e) => setBulkIssueDate(e.target.value)}
                        className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                      <p className="text-xs text-neutral-500 mt-1">La date limite de paiement sera calculée automatiquement (30 jours après cette date).</p>
                    </div>
                  )}
                  {!bulkFixIssueDate && (
                    <p className="text-xs text-neutral-500 pl-6">Non cochée par défaut. La date d'émission ne sera pas visible sur la facture du membre.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-neutral-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBulkDownloadModal(false);
                  setSelectedParticipants(new Set());
                }}
                className="px-4 py-2 text-neutral-700 hover:text-neutral-900 transition-colors"
                disabled={bulkDownloading}
              >
                Annuler
              </button>
              <button
                onClick={handleBulkDownload}
                disabled={bulkDownloading || selectedParticipants.size === 0}
                className="px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                <span>{bulkDownloading ? 'Génération...' : 'Télécharger'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showInvoice && selectedParticipantForInvoice && (
        <InvoiceTemplate
          isOpen={showInvoice}
          onClose={handleCloseInvoice}
          participant={selectedParticipantForInvoice}
          selectedPeriod={selectedPeriod}
          includeMembershipFee={selectedParticipantForInvoice.type === 'producer' || selectedParticipantForInvoice.type === 'prosumer' ? (bulkExcludeMembershipFee ? false : (bulkIncludeMembershipFee ? true : undefined)) : undefined}
          isAnnualRegularization={selectedParticipantForInvoice.type !== 'producer' ? isAnnualRegularization : undefined}
          companyNumberFilter={invoiceCompanyFilter}
          issueDate={bulkFixIssueDate ? bulkIssueDate : null}
        />
      )}

      {showReportModal && selectedParticipantForReport && (
        <ParticipantReportModal
          isOpen={showReportModal}
          onClose={() => {
            setShowReportModal(false);
            setSelectedParticipantForReport(null);
          }}
          participant={selectedParticipantForReport}
        />
      )}

      {showGroupReportModal && selectedGroupForReport && (
        <GroupReportModal
          isOpen={showGroupReportModal}
          onClose={() => {
            setShowGroupReportModal(false);
            setSelectedGroupForReport(null);
          }}
          groupName={selectedGroupForReport.name}
          participants={selectedGroupForReport.participants}
        />
      )}

      {showBulkReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-neutral-200">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold text-neutral-900 flex items-center space-x-2">
                    <BarChart3 className="w-6 h-6 text-amber-600" />
                    <span>Télécharger les rapports</span>
                  </h3>
                  <p className="text-sm text-neutral-600 mt-1">
                    Sélectionnez les participants et l'année du rapport
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowBulkReportModal(false);
                    setSelectedReportParticipants(new Set());
                  }}
                  className="text-neutral-500 hover:text-neutral-700 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-sm font-medium text-neutral-700">
                      Participants ({selectedReportParticipants.size} sélectionné{selectedReportParticipants.size > 1 ? 's' : ''})
                    </label>
                    <button
                      onClick={selectAllReportParticipants}
                      className="text-sm text-brand-teal hover:text-brand-teal/80 font-medium"
                    >
                      {selectedReportParticipants.size === participants.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                    </button>
                  </div>
                  <div className="border border-neutral-300 rounded-lg max-h-64 overflow-y-auto">
                    {sortedParticipants.map((participant) => (
                      <label
                        key={participant.id}
                        className="flex items-center p-3 hover:bg-neutral-50 cursor-pointer border-b border-neutral-200 last:border-b-0"
                      >
                        <input
                          type="checkbox"
                          checked={selectedReportParticipants.has(participant.id)}
                          onChange={() => toggleReportParticipantSelection(participant.id)}
                          className="w-4 h-4 text-brand-teal border-neutral-300 rounded focus:ring-brand-teal"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-neutral-900">{participant.name}</span>
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                participant.type === 'producer'
                                  ? 'bg-amber-100 text-amber-800'
                                  : participant.type === 'prosumer'
                                  ? 'bg-orange-100 text-orange-800'
                                  : 'bg-teal-100 text-teal-800'
                              }`}>
                                {participant.type === 'producer' ? 'Producteur' : participant.type === 'prosumer' ? 'Prosumer' : 'Consommateur'}
                              </span>
                            </div>
                            {participant.groupe && (
                              <span className="text-xs text-neutral-500">{participant.groupe}</span>
                            )}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Année du rapport
                  </label>
                  <select
                    value={bulkReportYear}
                    onChange={(e) => setBulkReportYear(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  >
                    <option value="2024">2024</option>
                    <option value="2025">2025</option>
                    <option value="2026">2026</option>
                  </select>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">
                    Un fichier ZIP sera généré contenant un rapport PDF pour chaque participant sélectionné.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-neutral-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBulkReportModal(false);
                  setSelectedReportParticipants(new Set());
                }}
                className="px-4 py-2 text-neutral-700 hover:text-neutral-900 transition-colors"
                disabled={bulkReportDownloading}
              >
                Annuler
              </button>
              <button
                onClick={handleBulkReportDownload}
                disabled={bulkReportDownloading || selectedReportParticipants.size === 0}
                className="px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                <span>{bulkReportDownloading ? 'Génération...' : 'Télécharger'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showExportData && selectedParticipantForExport && (
        <ExportDataModal
          isOpen={showExportData}
          onClose={() => {
            setShowExportData(false);
            setSelectedParticipantForExport(null);
          }}
          participant={selectedParticipantForExport}
        />
      )}

      {showValidationModal && selectedParticipantForValidation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <UserCheck className="w-6 h-6 text-amber-600" />
                  <h3 className="text-xl font-semibold text-neutral-900">
                    Validation du compte
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowValidationModal(false);
                    setSelectedParticipantForValidation(null);
                  }}
                  className="text-neutral-500 hover:text-neutral-700 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800 font-medium mb-2">
                    Demande de creation de compte en attente
                  </p>
                  <div className="space-y-2 text-sm text-amber-700">
                    <p><strong>Nom :</strong> {selectedParticipantForValidation.name}</p>
                    <p><strong>Email :</strong> {selectedParticipantForValidation.email}</p>
                    <p><strong>EAN :</strong> {selectedParticipantForValidation.ean_code}</p>
                    {selectedParticipantForValidation.groupe && (
                      <p><strong>Groupe :</strong> {selectedParticipantForValidation.groupe}</p>
                    )}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    En validant ce compte, le participant recevra un email de confirmation et pourra acceder a son espace membre.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => handleValidationAction('reject')}
                  disabled={validationLoading}
                  className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center space-x-2 disabled:opacity-50"
                >
                  <XCircle className="w-4 h-4" />
                  <span>Refuser</span>
                </button>
                <button
                  onClick={() => handleValidationAction('approve')}
                  disabled={validationLoading}
                  className="px-6 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
                  style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>{validationLoading ? 'Validation...' : 'Valider'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showValidationModal && selectedAccountForValidation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <UserCheck className="w-6 h-6 text-amber-600" />
                  <h3 className="text-xl font-semibold text-neutral-900">
                    Validation du compte
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowValidationModal(false);
                    setSelectedAccountForValidation(null);
                  }}
                  className="text-neutral-500 hover:text-neutral-700 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800 font-medium mb-2">
                    Nouvelle demande de compte en attente
                  </p>
                  <div className="space-y-2 text-sm text-amber-700">
                    <p><strong>Email :</strong> {selectedAccountForValidation.email}</p>
                    {selectedAccountForValidation.participant_name && (
                      <p><strong>Participant :</strong> {selectedAccountForValidation.participant_name}</p>
                    )}
                    {selectedAccountForValidation.ean_code && (
                      <p><strong>EAN :</strong> {selectedAccountForValidation.ean_code}</p>
                    )}
                    {selectedAccountForValidation.groupe && (
                      <p><strong>Groupe :</strong> {selectedAccountForValidation.groupe}</p>
                    )}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    En validant ce compte, le participant recevra un email de confirmation et pourra acceder a son espace membre.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => handleAccountValidationAction('reject_account')}
                  disabled={validationLoading}
                  className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center space-x-2 disabled:opacity-50"
                >
                  <XCircle className="w-4 h-4" />
                  <span>Refuser</span>
                </button>
                <button
                  onClick={() => handleAccountValidationAction('approve_account')}
                  disabled={validationLoading}
                  className="px-6 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
                  style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>{validationLoading ? 'Validation...' : 'Valider'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showInvitationModal && (
        <AccessInvitationModal
          participants={participants}
          onClose={() => setShowInvitationModal(false)}
        />
      )}
    </>
  );
}